import * as t from 'io-ts'
import {get} from 'object-path-immutable'
import React, {useEffect, useState, useMemo} from 'react'
import {
  AnalyticsEventType,
  AnalyticsEventTypeCodec,
  CardDefinition,
  CustomEventProperties,
  SDKLegacyEvents,
  nonCardAnalyticsEvents
} from '../../common/types'
import {ERROR_CODE_NON_UNIQUE} from '../../common/types/constants'
import {getKeyArray, sanitizeRecord} from '../../common/types/helpers'
import {BetaFlagMarker, PreviewFlagMarker} from '../../components/feature-flags'
import {FormSpacer} from '../../components/form'
import {Button, ButtonSegment} from '../../components/library/buttons'
import {CopyInput} from '../../components/library/copy'
import {
  DropdownBody,
  DropdownFooter,
  DropdownItem
} from '../../components/library/dropdown/content'
import {DropdownSelect} from '../../components/library/dropdown/select'
import {Select, TextInput} from '../../components/library/form'
import {Icon} from '../../components/library/icon/icon'
import {JSONField} from '../../components/library/json-field'
import {
  List,
  ListColumns,
  NoResultsPermissions,
  useListOrderBy
} from '../../components/library/list'
import {Modal} from '../../components/library/modal/modal'
import styled from 'styled-components'
import {Tag} from '../../components/library/tag/tag'
import {FormModal} from '../../components/modals'
import {OverflowFooter} from '../../components/overflow'
import {useCustomEvents} from '../../hooks/use-custom-events'
import {
  PermissionAction,
  PermissionResource,
  WebhookEndpointsOrderBy,
  WebhookRequestMethod,
  WebhookStatus
} from '../../data/workbench-api/generated/types'
import {useWebhookCredentialListQuery} from '../../data/workbench-api/generated/webhook-credential'
import {
  AddWebhookEndpointMutationVariables,
  UpdateWebhookEndpointMutationVariables,
  WebhookEndpointDetailFragment,
  useAddWebhookEndpointMutation,
  useDeleteWebhookEndpointMutation,
  useUpdateWebhookEndpointMutation,
  useWebhookEndpointListLazyQuery
} from '../../data/workbench-api/generated/webhook-endpoint'
import {EnvironmentNode} from '../../environment'
import {useFlags} from '../../hooks/use-flags'
import {useStateChanged} from '../../hooks/use-state-changed'
import {useAnalytics} from '../../lib/analytics/analytics'
import {upperCaseFirst} from '../../lib/helpers'
import {useToast} from '../../lib/toast'
import {cardTemplateVersionName} from '../card-builder/helpers'
import {Mono} from '../configuration/components'
import {useActionFlowConfig} from '../../hooks/use-action-flow-config'
import {WebhookMappingModal} from './mapping'
import {TestWebhookButton} from './test'

const webhookTypes = (cardScoped = false) => {
  const keySet: AnalyticsEventType[] = getKeyArray(AnalyticsEventTypeCodec)
  return Array.from(keySet)
    .sort()
    .filter(
      type =>
        (!cardScoped || !nonCardAnalyticsEvents.includes(type)) &&
        !SDKLegacyEvents.includes(type)
    )
}

interface WebhookListProps {
  environment: EnvironmentNode
  // if cardTemplateId is provided, we scope the UI to that card
  cardTemplateId?: string
  actionFlowConfigId?: string
  bleed?: string
  renderLayout?: (
    content: React.ReactElement,
    addNew: () => void
  ) => React.ReactElement
}

const WideCopyInput = styled(CopyInput)`
  min-width: 400px;
`

export const WebhookList = ({
  environment,
  cardTemplateId,
  actionFlowConfigId,
  renderLayout = content => content,
  bleed
}: WebhookListProps) => {
  const toaster = useToast()
  const analytics = useAnalytics()
  const canView = environment.hasPermission(
    PermissionResource.WebhooksEndpoint,
    PermissionAction.View
  )

  const canEdit = environment.hasPermission(
    PermissionResource.WebhooksEndpoint,
    PermissionAction.Edit
  )

  const [orderBy, setOrderBy] = useListOrderBy<WebhookEndpointsOrderBy>([
    WebhookEndpointsOrderBy.NameAsc
  ])

  const [query, {data, refetch = () => null, loading}] =
    useWebhookEndpointListLazyQuery({
      variables: {
        environmentId: environment.id,
        cardTemplateId,
        actionFlowConfigId,
        orderBy
      }
    })

  const [eventConfigMappings] = useCustomEvents(
    environment.organisationId,
    environment.id
  )

  useEffect(() => {
    if (canView) {
      query()
      analytics.track({
        event: 'Viewed webhook subscriptions configuration',
        properties: {
          org_id: environment.organisationId,
          environment_id: environment.id,
          category: 'configuration>webhooks>subscriptions'
        }
      })
    }
  }, [analytics, canView, environment.id, environment.organisationId, query])

  const [showAddModal, setShowAddModal] = useState(false)
  const [secretModal, setSecretModal] = useState<string>()
  const [editWebhookEndpoint, setEditWebhookEndpoint] =
    useState<WebhookEndpointDetailFragment>()
  const [editBatchInterval, setBatchInterval] =
    useState<WebhookEndpointDetailFragment>()
  const [editMapping, setEditMapping] =
    useState<WebhookEndpointDetailFragment | null>()

  const displayTypeList = (types?: (string | null | undefined)[] | null) => {
    if (!types || types.length === 0) {
      return ''
    }

    const maxCount = 3
    const prefixList =
      types.length <= maxCount ? types : types.slice(0, maxCount)
    const postFixCount = types.length <= maxCount ? 0 : types.length - maxCount

    const postFixText =
      postFixCount >= 1
        ? `and ${postFixCount} other${postFixCount === 1 ? '' : 's'}`
        : ''

    return (
      <span title={types?.join('\n')}>
        {prefixList.join(', ')} {postFixText}
      </span>
    )
  }

  return renderLayout(
    <>
      <List<WebhookEndpointDetailFragment, WebhookEndpointsOrderBy>
        setOrderBy={setOrderBy}
        orderBy={orderBy}
        columns={[
          {
            id: 'name',
            header: 'Name',
            accessor: r => (
              <a onClick={() => setEditWebhookEndpoint(r)}>
                {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                {r.name || 'Untitled'}
              </a>
            ),
            sortKey: WebhookEndpointsOrderBy.NameAsc
          },
          {
            id: 'url',
            header: 'URL',
            accessor: r => <WideCopyInput value={r.url} />
          },
          {
            id: 'enabled',
            header: 'Status',
            sortKey: WebhookEndpointsOrderBy.StatusAsc,
            accessor: r => (
              <Tag
                appearance={
                  r.status === WebhookStatus.Active ? 'green' : 'light'
                }
              >
                {upperCaseFirst(r.status.toLowerCase())}
              </Tag>
            )
          },
          {
            id: 'mapping',
            header: '',
            horizontalAlign: 'right',
            accessor: r => (
              <Button
                appearance="secondary"
                disabled={!canEdit}
                onClick={() => setEditMapping(r)}
                size="small"
              >
                Edit mapping
                <BetaFlagMarker />
              </Button>
            )
          },
          ...(!cardTemplateId
            ? ([
                {
                  id: 'card',
                  header: 'Card',
                  accessor: r => {
                    return r.cardTemplateId
                      ? get(r.cardTemplateDefinition, 'metadata.cardName') ??
                          `__ ${r.cardTemplateId}`
                      : 'All cards'
                  }
                }
              ] as ListColumns<
                WebhookEndpointDetailFragment,
                WebhookEndpointsOrderBy
              >)
            : []),
          {
            id: 'method',
            header: 'Method',
            accessor: r => <Mono>{r.requestMethod.toUpperCase()}</Mono>
          },
          {
            id: 'types',
            header: 'Types',
            accessor: r => displayTypeList(r.types)
          },
          {
            id: 'actions',
            header: '',
            horizontalAlign: 'right',
            accessor: r => (
              <ButtonSegment appearance="secondary">
                <Button
                  appearance="secondary"
                  onClick={() => setSecretModal(r.secret)}
                  size="small"
                >
                  Reveal secret
                </Button>
                <TestWebhookButton webhookEndpoint={r} />
              </ButtonSegment>
            )
          }
        ]}
        renderOverflow={(webhookEndpoint, closeDropdown) => (
          <Overflow
            environment={environment}
            webhookEndpoint={webhookEndpoint}
            setEditWebhookEndpoint={setEditWebhookEndpoint}
            setEditMapping={setEditMapping}
            setBatchInterval={setBatchInterval}
            canEdit={canEdit}
            canViewLogs={environment.hasPermission(
              PermissionResource.WebhookLog,
              PermissionAction.View
            )}
            closeDropdown={() => {
              closeDropdown()
              refetch()
            }}
          />
        )}
        rows={data?.webhookEndpoints?.nodes ?? []}
        loading={loading}
        bleed={bleed}
        renderEmpty={canView ? undefined : () => <NoResultsPermissions />}
      />

      {showAddModal ? (
        <AddModal
          environmentId={environment.id}
          organisationId={environment.organisationId}
          cardTemplateId={cardTemplateId}
          actionFlowConfigId={actionFlowConfigId}
          eventConfigMappings={eventConfigMappings}
          handleClose={() => {
            setShowAddModal(false)
          }}
          onCompleted={() => {
            analytics.track({
              event: 'Added configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category: 'configuration>webhooks>subscriptions'
              }
            })
            setShowAddModal(false)
            toaster('Webhook added')
            refetch()
          }}
        />
      ) : null}
      {editWebhookEndpoint ? (
        <EditModal
          webhookEndpoint={editWebhookEndpoint}
          eventConfigMappings={eventConfigMappings}
          handleClose={() => {
            setEditWebhookEndpoint(undefined)
          }}
          onCompleted={() => {
            analytics.track({
              event: 'Edited configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category: 'configuration>webhooks>subscriptions'
              }
            })
            setEditWebhookEndpoint(undefined)
            toaster('Webhook updated')
            refetch()
          }}
        />
      ) : null}
      {editMapping ? (
        <WebhookMappingModal
          webhookEndpoint={editMapping}
          eventConfigMappings={eventConfigMappings}
          handleClose={() => {
            setEditMapping(undefined)
            refetch()
          }}
        />
      ) : null}

      {secretModal ? (
        <Modal
          title="Payload signing secret"
          handleClose={() => {
            setSecretModal(undefined)
          }}
          style={{width: '600px'}}
        >
          <p>
            Payload data will be signed with this secret, the resulting
            signature is available to verify in the{' '}
            <code>vnd.io.atomic.signature</code> request header.
          </p>
          <CopyInput value={secretModal} />
        </Modal>
      ) : null}
      {editBatchInterval ? (
        <EditBatchIntervalModal
          webhookEndpoint={editBatchInterval}
          handleClose={() => {
            setBatchInterval(undefined)
          }}
          onCompleted={() => {
            analytics.track({
              event: 'Edited configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category: 'configuration>webhooks>subscriptions'
              }
            })
            setBatchInterval(undefined)
            toaster('Batch interval updated')
            refetch()
          }}
        />
      ) : null}
    </>,
    () => setShowAddModal(true)
  )
}

interface OverflowProps {
  webhookEndpoint: WebhookEndpointDetailFragment
  setEditWebhookEndpoint: (obj: WebhookEndpointDetailFragment) => void
  setEditMapping: (obj: WebhookEndpointDetailFragment) => void
  setBatchInterval: (obj: WebhookEndpointDetailFragment) => void
  environment: EnvironmentNode
  closeDropdown: () => void
  canEdit: boolean
  canViewLogs: boolean
}

const Overflow = ({
  webhookEndpoint,
  setEditWebhookEndpoint,
  setEditMapping,
  setBatchInterval,
  environment,
  closeDropdown,
  canEdit,
  canViewLogs
}: OverflowProps) => {
  const toaster = useToast()
  const analytics = useAnalytics()
  const [updateMutation] = useUpdateWebhookEndpointMutation({
    onError: error => {
      if (error.message.includes(ERROR_CODE_NON_UNIQUE)) {
        toaster('A duplicate of this webhook already enabled.', 'error')
      } else {
        toaster(error.message, 'error')
      }
    },
    onCompleted: () => {
      closeDropdown()
      toaster(
        webhookEndpoint.status === WebhookStatus.Active
          ? 'Webhook enabled'
          : 'Webhook disabled'
      )
      analytics.track({
        event: 'Edited configuration',
        properties: {
          org_id: environment.organisationId,
          environment_id: environment.id,
          category: 'configuration>webhooks>subscriptions'
        }
      })
    }
  })

  const {featurePreview} = useFlags(environment.organisationId)

  const [deleteMutation] = useDeleteWebhookEndpointMutation({
    onCompleted: () => {
      closeDropdown()
      toaster('Webhook deleted')
      analytics.track({
        event: 'Edited configuration',
        properties: {
          org_id: environment.organisationId,
          environment_id: environment.id,
          category: 'configuration>webhooks>subscriptions'
        }
      })
    }
  })

  const logUrl = `/${environment.organisationId}/${webhookEndpoint.environmentId}/webhook-requests?webhook=${webhookEndpoint.id}`

  return (
    <>
      <DropdownBody>
        <DropdownItem disabled={!canEdit}>
          <a
            onClick={() => {
              setEditWebhookEndpoint(webhookEndpoint)
              closeDropdown()
            }}
          >
            Edit
          </a>
        </DropdownItem>
        <DropdownItem disabled={!canEdit}>
          <a
            onClick={() => {
              setEditMapping(webhookEndpoint)
              closeDropdown()
            }}
          >
            Edit mapping
            <BetaFlagMarker />
          </a>
        </DropdownItem>

        {featurePreview ? (
          <DropdownItem disabled={!canEdit}>
            <a
              onClick={() => {
                setBatchInterval(webhookEndpoint)
                closeDropdown()
              }}
            >
              Edit batch interval
              <PreviewFlagMarker />
            </a>
          </DropdownItem>
        ) : null}

        <DropdownItem disabled={!canViewLogs}>
          <a
            href={logUrl}
            onClick={() => {
              closeDropdown()
              analytics.track({
                event: 'Redirected to external URL',
                properties: {
                  org_id: environment.organisationId,
                  environment_id: environment.id,
                  url: logUrl,
                  category: 'general'
                }
              })
            }}
            target="_blank"
            rel="noreferrer"
          >
            Request log <Icon name="external" style={{marginLeft: '10px'}} />
            <BetaFlagMarker />
          </a>
        </DropdownItem>

        <DropdownItem disabled={!canEdit}>
          <a
            onClick={() => {
              updateMutation({
                variables: {
                  id: webhookEndpoint.id,
                  environmentId: webhookEndpoint.environmentId,
                  types: webhookEndpoint.types?.map(String),
                  name: webhookEndpoint.name,
                  status:
                    webhookEndpoint.status !== WebhookStatus.Active
                      ? WebhookStatus.Active
                      : WebhookStatus.Disabled
                }
              })
            }}
          >
            {webhookEndpoint.status === WebhookStatus.Active
              ? 'Disable'
              : 'Enable'}
          </a>
        </DropdownItem>
        {webhookEndpoint.status !== WebhookStatus.Active ? (
          <DropdownItem disabled={!canEdit}>
            <a
              onClick={() => {
                if (confirm('Deletion is permanent. Are you sure?')) {
                  deleteMutation({
                    variables: {
                      id: webhookEndpoint.id,
                      environmentId: webhookEndpoint.environmentId
                    }
                  })
                }
              }}
            >
              Delete
            </a>
          </DropdownItem>
        ) : null}
      </DropdownBody>
      <DropdownFooter>
        <OverflowFooter
          id={webhookEndpoint.id}
          updated={webhookEndpoint.updated}
          created={webhookEndpoint.created}
        />
      </DropdownFooter>
    </>
  )
}

const getWebhookTypes = (cardScoped = false) =>
  webhookTypes(cardScoped).map(webhook => ({
    value: webhook,
    text: webhook
  }))

const isChangingEventTypeType = (
  updatedCardTemplateId: string | undefined,
  {cardTemplateId}: {cardTemplateId?: string | null}
) => {
  return (
    // update to custom event from card event
    (cardTemplateId &&
      (!updatedCardTemplateId || updatedCardTemplateId === 'CustomEvent')) ||
    // updating to card event from custom event
    (updatedCardTemplateId &&
      (!cardTemplateId || cardTemplateId === 'CustomEvent'))
  )
}

interface AddModalProps {
  environmentId: string
  organisationId: string
  cardTemplateId?: string
  actionFlowConfigId?: string
  eventConfigMappings: Record<string, CustomEventProperties>
  handleClose(): void
  onCompleted: () => void
}
export const AddModal = ({
  environmentId,
  cardTemplateId,
  actionFlowConfigId,
  handleClose,
  onCompleted,
  eventConfigMappings
}: AddModalProps) => {
  const allCardsFilterValue = useMemo(
    () => ({
      cardTemplateId: 'All cards',
      actionFlowConfigId: 'All flows'
    }),
    []
  )

  const [selectedCardTemplate, setSelectedCardTemplate] = useState<string>()

  const [localObj, setLocalObj, dirty] =
    useStateChanged<AddWebhookEndpointMutationVariables>({
      environmentId,
      types: [],
      url: '',
      headers: {},
      cardTemplateId,
      actionFlowConfigId,
      payloadMapping: null,
      requestMethod: WebhookRequestMethod.Post,
      name: ''
    })
  const [doMutation, {loading, error}] = useAddWebhookEndpointMutation({
    onCompleted
  })

  const {data} = useActionFlowConfig({environmentId})

  const cardTemplateOptions = useMemo(() => {
    const mappedTemplates =
      // eslint-disable-next-line functional/immutable-data
      data
        ?.flatMap(afNode => {
          // using get here because config is unknown because graphql JSON type
          const templates: Record<string, CardDefinition> | undefined = get(
            afNode.currentVersion?.config,
            'cardTemplates'
          )

          if (!templates) {
            return []
          }

          return Object.entries(templates).map(([key, ct]) => {
            return {
              // include af name and card template
              text: `${cardTemplateVersionName(ct.metadata.cardName)} (Flow: ${
                afNode.name || 'Untitled Action Flow'
              })`,
              // composite of the two
              value: JSON.stringify({
                cardTemplateId: key,
                actionFlowConfigId: afNode.id
              })
            }
          })
        })
        // if card templates were shareable between action flows, we would need to dedupe here (?)
        ?.sort((a, b) => a.text.localeCompare(b.text)) ?? []

    // adding the all card value
    // eslint-disable-next-line functional/immutable-data
    mappedTemplates.unshift({
      text: allCardsFilterValue.cardTemplateId,
      value: JSON.stringify(allCardsFilterValue)
    })

    return mappedTemplates
  }, [data, allCardsFilterValue])

  const webhookCredentialQuery = useWebhookCredentialListQuery({
    variables: {environmentId}
  })
  const webhookCredentials =
    webhookCredentialQuery.data?.webhookCredentials?.nodes

  const customEventNames = Object.keys(eventConfigMappings)

  const friendlyError = error
    ? error?.message.includes(ERROR_CODE_NON_UNIQUE)
      ? `URL ${localObj.url} is already subscribed to ${localObj.types}`
      : error
    : null

  const eventTypes = [
    ...customEventNames.map(n => ({text: n, value: n})),
    ...getWebhookTypes(
      !!localObj.cardTemplateId &&
        localObj.cardTemplateId !== allCardsFilterValue.cardTemplateId
    )
  ].sort((a, b) => a.text.localeCompare(b.text))

  const valid = !!(
    !!localObj.types?.length &&
    localObj.url &&
    localObj.requestMethod &&
    !!localObj.cardTemplateId &&
    !!localObj.actionFlowConfigId &&
    !localObj.url.match(/http\s+/)
  )

  return (
    <FormModal
      title="Add webhook subscription"
      handleClose={handleClose}
      onSubmit={() => {
        const variables = {...localObj}
        if (variables.cardTemplateId === allCardsFilterValue.cardTemplateId) {
          delete variables.cardTemplateId
        }
        if (
          variables.actionFlowConfigId ===
          allCardsFilterValue.actionFlowConfigId
        ) {
          delete variables.actionFlowConfigId
        }

        return doMutation({variables})
      }}
      error={friendlyError}
      isValid={dirty && !loading && valid}
    >
      {!cardTemplateId ? (
        <>
          <Select
            label="Select card..."
            upLabel="Card"
            options={cardTemplateOptions}
            value={selectedCardTemplate}
            firstOptionText="..."
            onChange={input => {
              // this is because we use a composite value
              setSelectedCardTemplate(input)

              // we control this so will know it is valid
              const parsedInput = JSON.parse(input)

              // if we change back to all cards from custom event, clear out the previous set types
              setLocalObj(c => ({
                ...c,
                cardTemplateId: parsedInput.cardTemplateId,
                actionFlowConfigId: parsedInput.actionFlowConfigId,
                types: isChangingEventTypeType(
                  parsedInput.cardTemplateId,
                  c ?? {}
                )
                  ? undefined
                  : c.types
              }))
            }}
          />
          <FormSpacer />
        </>
      ) : null}

      <TextInput
        upLabel="Webhook name"
        label="Enter webhook name..."
        value={localObj.name}
        onChange={name => setLocalObj(c => ({...c, name}))}
      />
      <FormSpacer />
      <DropdownSelect<AnalyticsEventType | string>
        isMultiSelect={true}
        order="up"
        label="Select event types..."
        upLabel="Registered event types"
        value={localObj.types as AnalyticsEventType[]}
        options={eventTypes}
        onChange={types => setLocalObj(c => ({...c, types}))}
      />

      <FormSpacer />
      <RequestMethodField
        value={localObj.requestMethod}
        onChange={(requestMethod = WebhookRequestMethod.Post) => {
          setLocalObj(c => ({...c, requestMethod}))
        }}
      />
      <FormSpacer />
      <TextInput
        upLabel="URL"
        label="Enter URL..."
        value={localObj.url}
        onChange={url => setLocalObj(c => ({...c, url: url.trim()}))}
      />
      <FormSpacer />
      <HeadersField
        value={sanitizeRecord(localObj.headers, String)}
        onChange={headers => setLocalObj(c => ({...c, headers}))}
      />
      <FormSpacer />
      <Select
        label="Select credential..."
        upLabel="Credential"
        options={
          webhookCredentials?.map(c => ({
            value: c.id,
            text: c.name
          })) ?? []
        }
        value={localObj.webhookCredentialId ?? undefined}
        onChange={webhookCredentialId => {
          setLocalObj(c => ({...c, webhookCredentialId}))
        }}
      />
    </FormModal>
  )
}

interface EditModalProps {
  webhookEndpoint: WebhookEndpointDetailFragment
  handleClose: () => void
  onCompleted: () => void
  eventConfigMappings: Record<string, CustomEventProperties>
}

export const EditModal = ({
  webhookEndpoint,
  handleClose,
  onCompleted,
  eventConfigMappings
}: EditModalProps) => {
  const [localObj, setLocalObj, dirty] =
    useStateChanged<UpdateWebhookEndpointMutationVariables>(webhookEndpoint)
  const [doMutation, {loading, error}] = useUpdateWebhookEndpointMutation({
    onCompleted
  })

  const webhookCredentialQuery = useWebhookCredentialListQuery({
    variables: {environmentId: webhookEndpoint.environmentId}
  })
  const webhookCredentials =
    webhookCredentialQuery.data?.webhookCredentials?.nodes

  const customEventNames = Object.keys(eventConfigMappings)
  const eventTypes = [
    ...customEventNames.map(n => ({text: n, value: n})),
    ...getWebhookTypes(!!webhookEndpoint.cardTemplateId)
  ].sort((a, b) => a.text.localeCompare(b.text))

  const valid = !!localObj.requestMethod

  return (
    <FormModal
      title="Edit webhook endpoint"
      handleClose={handleClose}
      onSubmit={() =>
        doMutation({
          variables: localObj
        })
      }
      error={error}
      isValid={dirty && !loading && valid}
    >
      <TextInput
        upLabel="Name"
        label="Enter name..."
        value={localObj.name ?? undefined}
        onChange={name => setLocalObj(c => ({...c, name}))}
      />
      <FormSpacer />
      <DropdownSelect<AnalyticsEventType | string>
        isMultiSelect={true}
        order="up"
        label="Select webhook types..."
        upLabel="Webhook types"
        value={localObj.types as AnalyticsEventType[]}
        options={eventTypes}
        onChange={types => setLocalObj(c => ({...c, types}))}
      />
      <FormSpacer />
      <RequestMethodField
        value={localObj.requestMethod ?? undefined}
        onChange={requestMethod => {
          setLocalObj(c => ({...c, requestMethod}))
        }}
      />
      <FormSpacer />
      <HeadersField
        value={sanitizeRecord(localObj.headers, String)}
        onChange={headers => setLocalObj(c => ({...c, headers}))}
      />
      <>
        <FormSpacer />
        <Select
          label="Select credential..."
          upLabel="Credential"
          options={
            webhookCredentials?.map(c => ({
              value: c.id,
              text: c.name
            })) ?? []
          }
          value={localObj.webhookCredentialId ?? undefined}
          onChange={webhookCredentialId => {
            setLocalObj(c => ({
              ...c,
              webhookCredentialId: webhookCredentialId ?? null
            }))
          }}
        />
      </>
    </FormModal>
  )
}

interface EditBatchIntervalModalProps {
  webhookEndpoint: WebhookEndpointDetailFragment
  handleClose: () => void
  onCompleted: () => void
}

export const EditBatchIntervalModal = ({
  webhookEndpoint,
  handleClose,
  onCompleted
}: EditBatchIntervalModalProps) => {
  const [localObj, setLocalObj, dirty] =
    useStateChanged<UpdateWebhookEndpointMutationVariables>(webhookEndpoint)
  const [doMutation, {loading, error}] = useUpdateWebhookEndpointMutation({
    onCompleted
  })

  const valid =
    Number.isInteger(localObj.batchInterval) || localObj.batchInterval === null

  return (
    <FormModal
      title="Edit batch interval"
      handleClose={handleClose}
      onSubmit={() =>
        doMutation({
          variables: localObj
        })
      }
      error={error}
      isValid={dirty && !loading && valid}
    >
      <TextInput
        upLabel="Batch interval in minutes"
        label="Enter number of minutes..."
        value={localObj.batchInterval?.toString() ?? ''}
        onChange={batchInterval => {
          setLocalObj(c => ({
            ...c,
            batchInterval: parseInt(batchInterval) || null
          }))
        }}
      />
    </FormModal>
  )
}

const HeadersCodec = t.record(t.string, t.string)

interface HeadersFieldProps {
  value: Record<string, string>
  onChange: (h: Record<string, string>) => void
}
const HeadersField = ({value, onChange}: HeadersFieldProps) => (
  <JSONField
    style={{height: '100px'}}
    label="Headers"
    value={value}
    raw={true}
    onChange={onChange}
    codec={HeadersCodec}
    legacyMode={true}
  />
)

interface RequestMethodFieldProps {
  value?: WebhookRequestMethod
  onChange: (h: WebhookRequestMethod | undefined) => void
}
const RequestMethodField = ({value, onChange}: RequestMethodFieldProps) => (
  <Select<WebhookRequestMethod>
    upLabel="Request method"
    label="Select request method..."
    options={[
      {
        value: WebhookRequestMethod.Post,
        text: 'POST'
      },
      {
        value: WebhookRequestMethod.Put,
        text: 'PUT'
      }
    ]}
    value={value}
    onChange={onChange}
  />
)
